// dashboard-frontend/src/App.js (atualização)
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "./config";
import { getStoredAuth, setStoredAuth, setupAuth } from "./api";
import Login from "./components/Login";
import ChangePassword from "./components/ChangePassword";
import MetricsDashboard from "./components/MetricsDashboard";
import ExportConversations from "./components/ExportConversations";
import Clients from "./components/Clients";
import Customization from "./components/Customization";
import LeadsManager from "./components/LeadsManager";
import BotsList from "./components/BotsList";
import TrainAgent from "./components/TrainAgent";
import PersonaNaPratica from "./components/PersonaNaPratica";
import BotPayment from "./components/BotPayment";
import SupportTickets from "./components/SupportTickets";

const App = () => {
  const [auth, setAuth] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [authErrorFromUrl, setAuthErrorFromUrl] = useState(null);

  // Ao montar: restaurar token do localStorage ou da URL (?token= após login com Google) e validar com /api/auth/me
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    const authError = params.get("auth_error");

    if (tokenFromUrl) {
      setStoredAuth({ token: tokenFromUrl, user: null });
      setupAuth(tokenFromUrl);
      window.history.replaceState({}, "", window.location.pathname || "/");
      axios
        .get(`${API_BASE}/api/auth/me`)
        .then((res) => {
          setStoredAuth({ token: tokenFromUrl, user: res.data });
          setAuth({ token: tokenFromUrl, user: res.data });
        })
        .catch(() => {
          setStoredAuth(null);
          setAuth(null);
        })
        .finally(() => setAuthChecked(true));
      return;
    }

    if (authError) {
      window.history.replaceState({}, "", window.location.pathname || "/");
      const messages = {
        access_denied: "Login com Google cancelado.",
        no_code: "Resposta inválida do Google.",
        no_email: "Conta Google sem e-mail disponível.",
        server_error: "Erro no servidor. Tente de novo.",
        google_not_configured: "Login com Google não está configurado.",
      };
      setAuthErrorFromUrl(messages[authError] || "Falha no login com Google.");
      setAuthChecked(true);
      return;
    }

    const stored = getStoredAuth();
    if (!stored) {
      setAuthChecked(true);
      return;
    }
    setupAuth(stored.token);
    axios
      .get(`${API_BASE}/api/auth/me`)
      .then((res) => {
        setAuth({ token: stored.token, user: res.data });
      })
      .catch(() => {
        setStoredAuth(null);
        setAuth(null);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  // Ouvir 401 global (sessão expirada)
  useEffect(() => {
    const onUnauthorized = () => {
      setStoredAuth(null);
      setAuth(null);
    };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, []);

  const handleLogout = () => {
    setStoredAuth(null);
    setAuth(null);
  };

  const renderPage = () => {
    const user = auth?.user;
    switch (currentPage) {
      case "dashboard":
        return <MetricsDashboard isAdmin={isAdmin} />;
      case "export":
        return <ExportConversations />;
      case "clients":
        return <Clients user={user} />;
      case "bots":
        return <BotsList />;
      case "train":
        return <TrainAgent />;
      case "persona":
        return <PersonaNaPratica />;
      case "customization":
        return <Customization user={user} />;
      case "leads":
        return <LeadsManager />;
      case "botPayment":
        return <BotPayment />;
      case "support":
        return <SupportTickets />;
      case "changePassword":
        return (
          <ChangePassword
            onSuccess={() => setCurrentPage("dashboard")}
          />
        );
      default:
        return <MetricsDashboard />;
    }
  };

  if (!authChecked) {
    return (
      <div style={{ ...styles.container, justifyContent: "center", alignItems: "center" }}>
        <div style={styles.subtitle}>Carregando...</div>
      </div>
    );
  }

  if (!auth) {
    return <Login onSuccess={setAuth} initialError={authErrorFromUrl} />;
  }

  // Primeiro acesso: obrigar troca de senha (conta criada pelo Stripe)
  if (auth.user?.mustChangePassword) {
    return (
      <ChangePassword
        forceFirstAccess
        onSuccess={() => {
          axios.get(`${API_BASE}/api/auth/me`).then((res) => {
            setAuth({ token: auth.token, user: res.data });
          });
        }}
      />
    );
  }

  const isAdmin = auth.user?.role === "admin";
  const isBotPaymentAdmin = auth.user?.email === "contato@empreendera.com";

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <span style={styles.titleIcon}>🚀</span>
          TILTI DASHBOARD
          <span style={styles.titleGlow}></span>
        </h1>
        <div style={styles.subtitle}>
          Painel de Controle da Espaçonave
          {auth.user?.email && (
            <span style={{ marginLeft: 16, opacity: 0.9 }}>
              {auth.user.email} {isAdmin ? "(Admin)" : ""}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleLogout}
          style={styles.logoutButton}
        >
          Sair
        </button>
      </div>

      <div style={styles.nav}>
        <button
          style={{ ...styles.navButton, ...(currentPage === "dashboard" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("dashboard")}
        >
          <span style={styles.navIcon}>📊</span>
          Dashboard
        </button>
        <button
          style={{ ...styles.navButton, ...(currentPage === "export" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("export")}
        >
          <span style={styles.navIcon}>💾</span>
          Conversas
        </button>
        {isAdmin && (
          <button
            style={{ ...styles.navButton, ...(currentPage === "clients" ? styles.navButtonActive : {}) }}
            onClick={() => setCurrentPage("clients")}
          >
            <span style={styles.navIcon}>👥</span>
            Clientes
          </button>
        )}
        <button
          style={{ ...styles.navButton, ...(currentPage === "bots" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("bots")}
        >
          <span style={styles.navIcon}>🤖</span>
          Bots
        </button>
        <button
          style={{ ...styles.navButton, ...(currentPage === "train" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("train")}
        >
          <span style={styles.navIcon}>🎓</span>
          Treine seu agente
        </button>
        <button
          style={{ ...styles.navButton, ...(currentPage === "persona" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("persona")}
        >
          <span style={styles.navIcon}>👤</span>
          Persona na prática
        </button>
        <button
          style={{ ...styles.navButton, ...(currentPage === "customization" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("customization")}
        >
          <span style={styles.navIcon}>🎨</span>
          Customização
        </button>
        <button
          style={{ ...styles.navButton, ...(currentPage === "leads" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("leads")}
        >
          <span style={styles.navIcon}>⭐</span>
          Leads
        </button>
        {!isAdmin && (
          <button
            style={{ ...styles.navButton, ...(currentPage === "support" ? styles.navButtonActive : {}) }}
            onClick={() => setCurrentPage("support")}
          >
            <span style={styles.navIcon}>🛟</span>
            Suporte
          </button>
        )}
        {isBotPaymentAdmin && (
          <button
            style={{ ...styles.navButton, ...(currentPage === "botPayment" ? styles.navButtonActive : {}) }}
            onClick={() => setCurrentPage("botPayment")}
          >
            <span style={styles.navIcon}>💳</span>
            Pagamento do BOT
          </button>
        )}
        <button
          style={{ ...styles.navButton, ...(currentPage === "changePassword" ? styles.navButtonActive : {}) }}
          onClick={() => setCurrentPage("changePassword")}
        >
          <span style={styles.navIcon}>🔑</span>
          Trocar senha
        </button>
      </div>

      <div style={styles.content}>{renderPage()}</div>
    </div>
  );
};

// Estilos futuristas - Tema Espaçonave
const styles = {
  container: {
    padding: "30px 20px",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    maxWidth: "1400px",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
    minHeight: "100vh",
  },
  header: {
    textAlign: "center",
    marginBottom: "40px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logoutButton: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(0, 212, 255, 0.3)",
    borderRadius: "8px",
    color: "#B0BEC5",
    cursor: "pointer",
    fontSize: "14px",
  },
  title: {
    fontSize: "3rem",
    fontWeight: "800",
    background: "linear-gradient(135deg, #B026FF 0%, #00D4FF 50%, #B026FF 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "10px",
    textTransform: "uppercase",
    letterSpacing: "3px",
    position: "relative",
    display: "inline-block",
    textShadow: "0 0 30px rgba(0, 212, 255, 0.5)",
    animation: "pulseGlow 3s ease-in-out infinite",
  },
  titleIcon: {
    display: "inline-block",
    marginRight: "15px",
    fontSize: "2.5rem",
    animation: "float 3s ease-in-out infinite",
  },
  titleGlow: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "120%",
    height: "120%",
    background: "radial-gradient(circle, rgba(0, 212, 255, 0.3) 0%, transparent 70%)",
    borderRadius: "50%",
    zIndex: -1,
    animation: "pulseGlow 3s ease-in-out infinite",
  },
  subtitle: {
    fontSize: "1.1rem",
    color: "#B0BEC5",
    letterSpacing: "2px",
    textTransform: "uppercase",
    fontWeight: "300",
  },
  nav: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "15px",
    marginBottom: "40px",
    padding: "20px",
    background: "rgba(10, 14, 39, 0.6)",
    backdropFilter: "blur(10px)",
    borderRadius: "15px",
    border: "1px solid rgba(0, 212, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(0, 212, 255, 0.1)",
  },
  navButton: {
    margin: "0 5px",
    padding: "12px 24px",
    background: "linear-gradient(135deg, rgba(176, 38, 255, 0.5) 0%, rgba(10, 14, 39, 0.8) 100%)",
    color: "#B0BEC5",
    border: "1px solid rgba(0, 212, 255, 0.3)",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "1px",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    position: "relative",
    overflow: "hidden",
  },
  navButtonActive: {
    background: "linear-gradient(135deg, #B026FF 0%, #0066FF 100%)",
    color: "#FFFFFF",
    border: "1px solid #00D4FF",
    boxShadow: "0 0 20px rgba(255, 107, 53, 0.5), 0 0 40px rgba(0, 212, 255, 0.3)",
    transform: "translateY(-2px)",
  },
  navIcon: {
    fontSize: "18px",
  },
  content: {
    marginTop: "30px",
    position: "relative",
    zIndex: 1,
  },
};

export default App;
